import {
    useState,
    useEffect,
    useCallback,
    MouseEventHandler,
    KeyboardEventHandler,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { TabsProps } from '@mui/material/Tabs';
import { useStore } from '@ui/hooks/useStore';
import useWindowSize from '@ui/hooks/useWindowSize';
import { IState } from './IState';
import { rootStore } from '@store/Root.store';

const useHeader = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const xsBreakpoint = 1000;
    const { user, lang, devices, app } = useStore();
    const windowSize = useWindowSize();

    const initState: IState = {
        settingsVisible: false,
        statisticsVisible: false,
        mainMenuVisible: false,
        techSupportVisible: false,
        loginOldDevicesVisible: false,
        AddOldDeviceCloseVisible: false,
        ExistOldLoginVisible: false,
        modeSwitch: false,
        desktopMode: false,
        devSettingsCallback: () => {},
        isSettingsCloseCall: false,
    };
    const [state, setState] = useState(initState);

    useEffect(() => {
        setState((prev) => {
            const desktopMode = windowSize.width >= xsBreakpoint;
            const modeSwitch = prev.desktopMode !== desktopMode;
            return {
                ...prev,
                desktopMode,
                modeSwitch,
            };
        });
    }, [windowSize.width]);
    useEffect(() => {
        setTimeout(resetModeSwitch, 2000);
    }, [state.modeSwitch]);

    const resetModeSwitch = useCallback(() => {
        setState((prev) => ({ ...prev, modeSwitch: false }));
    }, []);
    const handlePageChange: TabsProps['onChange'] = useCallback(
        (e: React.SyntheticEvent<Element, Event>, page: string) => {
            const callback = () => {
                const search = new URLSearchParams(location.search);
                if (page === 'history' || page === 'support') {
                    const imei = localStorage.getItem('selectedDevice');
                    if (imei) search.set('sd', imei);
                    else search.delete('sd');
                    search.delete('sg');
                } else if (page === '') {
                    const id = localStorage.getItem('groupId');
                    if (id) search.set('sg', id);
                    else search.delete('sg');
                    search.delete('sd');
                } else {
                    search.delete('sd');
                    search.delete('sg');
                }
                navigate(`/${page}?${search.toString()}`);
            };
            if (!app.deviceSettingsVisible) {
                callback();
            } else {
                setState((prev) => ({ ...prev, isSettingsCloseCall: true }));
                setState((prev) => ({
                    ...prev,
                    devSettingsCallback: callback,
                }));
                app.setDeviceSettingsClosing();
            }
        },
        [location.search],
    );
    const handleTabClick: MouseEventHandler<HTMLElement> = useCallback((e) => {
        e.preventDefault();
    }, []);
    const handleLogOut = useCallback(() => {
        user.logout();
        app.resetDemoMode();
        localStorage.setItem('groupId', '');
        localStorage.setItem('selectedDevice', '');
    }, []);
    const handleTechSupportOpen = useCallback(() => {
        setState((prev) => ({ ...prev, techSupportVisible: true }));
    }, []);
    const handleTechSupportClose = useCallback(() => {
        setState((prev) => ({ ...prev, techSupportVisible: false }));
    }, []);
    const handleLoginOldDevicesOpen = useCallback(() => {
        if (rootStore.user.userLogin !== 'demo'){
            setState((prev) => ({ ...prev, loginOldDevicesVisible: true }));
        }
        else {
            setState((prev) => ({ ...prev, loginOldDevicesVisible: false }));
        }
    }, []);
    const handleLoginOldDevicesClose = useCallback(() => {
        setState((prev) => ({ ...prev, loginOldDevicesVisible: false })); //////////////////////
    }, []);
    const handleAddOldDeviceOpen = useCallback(() => {
        setState((prev) => ({ ...prev, AddOldDeviceCloseVisible: true })); //////////////////////
    }, []);
    const handleAddOldDeviceClose = useCallback(() => {
        setState((prev) => ({ ...prev, AddOldDeviceCloseVisible: false })); //////////////////////
    }, []);

    const handleExistOldLoginClose = useCallback(() => {
        setState((prev) => ({ ...prev, ExistOldLoginVisible:false }));
    }, []);///////
    const handleExistOldLoginOpen = useCallback(() => {
        setState((prev) => ({ ...prev, ExistOldLoginVisible:true }));
    }, []);///////

    const handleSettingsOpen = useCallback(() => {
        setState((prev) => ({ ...prev, settingsVisible: true }));
    }, []);
    const handleSettingsClose = useCallback(() => {
        setState((prev) => ({ ...prev, settingsVisible: false }));
    }, []);
    const handleStatisticsOpen = useCallback(() => {
        setState((prev) => ({ ...prev, statisticsVisible: true }));
    }, []);
    const handleStatisticsClose = useCallback(() => {
        setState((prev) => ({ ...prev, statisticsVisible: false }));
    }, []);
    const handleMainMenuBtnClick = useCallback(() => {
        setState((prev) => ({
            ...prev,
            mainMenuVisible: !prev.mainMenuVisible,
        }));
    }, []);
    const handleKeyDown: KeyboardEventHandler<HTMLElement> = (e) => {
        const { key } = e;
        if (key === 'Enter') {
            e.stopPropagation();
            e.preventDefault();
            const target = e.target as HTMLElement;
            target.click();
        }
    };
    const handlePrivacyPolicy = useCallback(() => {
        const URL = "https://fmsvega.ru/content/pdpp.pdf";
        window.open(URL, '_blank')?.focus();
    }, []);

    const handlers = {
        handlePrivacyPolicy,
        handleLogOut,
        handleSettingsOpen,
        handleSettingsClose,
        handleStatisticsOpen,
        handleStatisticsClose,
        handleMainMenuBtnClick,
        handlePageChange,
        handleKeyDown,
        handleTabClick,
        handleTechSupportOpen,
        handleTechSupportClose,
        handleLoginOldDevicesOpen,
        handleLoginOldDevicesClose,
        handleAddOldDeviceClose,
        handleAddOldDeviceOpen,
        handleExistOldLoginClose,
        handleExistOldLoginOpen
    };
    
    useEffect(() => {
        if (app.isDeviceSettingsClosed && state.isSettingsCloseCall) {
            state.devSettingsCallback();
            app.resetDeviceSettingsClosed();
            setState((prev) => ({ ...prev, isSettingsCloseCall: false }));
        }
    }, [app.isDeviceSettingsClosed]);

    return {
        user,
        app,
        devices,
        lang,
        state,
        handlers,
    };
};

export default useHeader;
